import { twApi } from '../api';
import { PaginateResponse } from '../../Global.model';

import {
  InvoiceService,
  CreateInvoiceServiceParams,
  UpdateInvoiceServiceParams,
  SearchInvoiceServicesParams,
  UpdateInvoiceServiceStatusParams,
  InvoiceServiceSearchFilters
} from './invoiceService.model';

/**
 * Api endpoints for Invoice Service entity
 */
const invoiceServiceApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getInvoiceServices: builder.query<
      PaginateResponse<InvoiceService>,
      { queryParams?: SearchInvoiceServicesParams; filters?: InvoiceServiceSearchFilters }
    >({
      query: ({ queryParams, filters = {} }) => {
        const params = { ...(queryParams || {}), limit: queryParams?.limit || 1000 };
        return {
          url: '/v3/invoice-services/search',
          method: 'POST',
          params: params,
          body: filters
        };
      },
      transformResponse: (response: { data: InvoiceService[]; total: number }) => {
        return {
          content: response.data,
          total: response.total
        };
      },
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'InvoiceService' as const, id })),
              { type: 'InvoiceService', id: 'INVOICE_SERVICE_LIST' }
            ]
          : [{ type: 'InvoiceService', id: 'INVOICE_SERVICE_LIST' }]
    }),

    addInvoiceService: builder.mutation<InvoiceService, CreateInvoiceServiceParams>({
      query: (arg) => ({
        url: '/v3/invoice-services',
        body: { ...arg },
        method: 'POST'
      }),
      transformResponse: (response: { data: InvoiceService }) => response.data,
      invalidatesTags: [{ type: 'InvoiceService', id: 'INVOICE_SERVICE_LIST' }]
    }),

    getInvoiceServiceById: builder.query<InvoiceService, string>({
      query: (id) => {
        return {
          url: `/v3/invoice-services/${id}`,
          method: 'GET'
        };
      },
      transformResponse: (response: { data: InvoiceService }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: 'InvoiceService' as const, id }]
    }),

    updateInvoiceService: builder.mutation<InvoiceService, UpdateInvoiceServiceParams>({
      query: (params) => {
        const { id, default_rate, ...updateParams } = params;
        return {
          url: `/v3/invoice-services/${id}`,
          method: 'PUT',
          body: { ...updateParams, default_rate: default_rate || null }
        };
      },
      invalidatesTags: (result, error, data) => [
        { type: 'InvoiceService', id: data.id },
        { type: 'InvoiceService', id: 'INVOICE_SERVICE_LIST' }
      ]
    }),

    updateInvoiceServiceStatus: builder.mutation<InvoiceService, UpdateInvoiceServiceParams>({
      query(params) {
        const { id } = params;
        return {
          url: `/v3/invoice-services/${id}`,
          method: 'PUT',
          body: { ...params }
        };
      },
      invalidatesTags: (result, error, data) => [
        { type: 'InvoiceService', id: 'INVOICE_SERVICE_LIST' }
      ]
    }),

    deleteInvoiceService: builder.mutation<InvoiceService, string>({
      query: (id) => ({
        url: `/v3/invoice-services/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'InvoiceService', id },
        { type: 'InvoiceService', id: 'INVOICE_SERVICE_LIST' }
      ]
    })
  }),
  overrideExisting: false
});

export const {
  useGetInvoiceServicesQuery,
  useAddInvoiceServiceMutation,
  useDeleteInvoiceServiceMutation,
  useGetInvoiceServiceByIdQuery,
  useUpdateInvoiceServiceMutation,
  useUpdateInvoiceServiceStatusMutation,
  useLazyGetInvoiceServicesQuery
} = invoiceServiceApi;
